import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { ScheduleDataStatus } from "../partial/ScheduleDataStatusIndicator.component";

/** Visit-only workplace rows come from `/booking-schedule/visit-only`; strip any that still appear on place data. */
export function placeScheduleWithoutVisitOnly(
  placeScheduleData: BookingScheduleInterface[] | undefined
): BookingScheduleInterface[] {
  return (placeScheduleData ?? []).filter(s => s.isVisitOnly !== true);
}

/**
 * @param placeZoneScheduleDataStatus: ScheduleDataStatus is deprecated for now
 * @param placeZoneScheduleDataForEnterprisaStatus: ScheduleDataStatus is deprecated for now
 * @param hasVehicleScheduleEnabled When false, vehicle schedule is not fetched (no license); vehicle status is ignored for combined status.
 * @param visitorManagementEnabled When false, visit-only schedule is not fetched; visit-only status is ignored for combined status.
 */
export const updateScheduleDataStatus = ({
  enabled: {
    workplaceBookingEnabled,
    hasVehicleLicense,
    hasConferenceLicense,
    visitorManagementEnabled
  },
  status: {
    isScheduleLoading,
    placeScheduleDataStatus,
    visitOnlyScheduleDataStatus,
    noPlaceZoneBookingScheduleDataStatus,
    vehicleScheduleDataStatus,
    setScheduleDataStatus
  }
}: {
  enabled: {
    workplaceBookingEnabled: boolean;
    hasVehicleLicense: boolean;
    hasConferenceLicense: boolean;
    visitorManagementEnabled: boolean;
  };
  status: {
    isScheduleLoading: boolean;
    placeScheduleDataStatus: ScheduleDataStatus;
    visitOnlyScheduleDataStatus: ScheduleDataStatus;
    noPlaceZoneBookingScheduleDataStatus: ScheduleDataStatus;
    vehicleScheduleDataStatus: ScheduleDataStatus;
    setScheduleDataStatus: (s: ScheduleDataStatus) => void;
  };
}) => {
  const placeLoading = workplaceBookingEnabled && placeScheduleDataStatus === "loading";
  const placeError = workplaceBookingEnabled && placeScheduleDataStatus === "error";
  const placeSuccess = !workplaceBookingEnabled || placeScheduleDataStatus === "success";

  const vehicleLoading = hasVehicleLicense && vehicleScheduleDataStatus === "loading";
  const vehicleError = hasVehicleLicense && vehicleScheduleDataStatus === "error";
  const vehicleSuccess = !hasVehicleLicense || vehicleScheduleDataStatus === "success";

  const visitOnlyLoading = visitorManagementEnabled && visitOnlyScheduleDataStatus === "loading";
  const visitOnlyError = visitorManagementEnabled && visitOnlyScheduleDataStatus === "error";
  const visitOnlySuccess = !visitorManagementEnabled || visitOnlyScheduleDataStatus === "success";

  const conferenceLoading =
    hasConferenceLicense && noPlaceZoneBookingScheduleDataStatus === "loading";
  const conferenceError = hasConferenceLicense && noPlaceZoneBookingScheduleDataStatus === "error";
  const conferenceSuccess =
    !hasConferenceLicense || noPlaceZoneBookingScheduleDataStatus === "success";

  if (placeLoading || conferenceLoading || vehicleLoading || isScheduleLoading)
    return setScheduleDataStatus("loading");

  if (placeError || conferenceError || vehicleError) return setScheduleDataStatus("error");

  if (placeSuccess && conferenceSuccess && vehicleSuccess) return setScheduleDataStatus("success");
  if (
    placeScheduleDataStatus === "loading" ||
    visitOnlyLoading ||
    conferenceLoading ||
    vehicleLoading ||
    isScheduleLoading
  )
    return setScheduleDataStatus("loading");

  if (placeScheduleDataStatus === "error" || visitOnlyError || conferenceError || vehicleError)
    return setScheduleDataStatus("error");

  if (
    placeScheduleDataStatus === "success" &&
    visitOnlySuccess &&
    conferenceSuccess &&
    vehicleSuccess
  )
    return setScheduleDataStatus("success");
};

export const updateScheduleData = (
  placeScheduleData: BookingScheduleInterface[] | undefined,
  visitOnlyScheduleData: BookingScheduleInterface[] | undefined,
  noPlaceZoneBookingScheduleData: BookingScheduleInterface[] | undefined, // conference room
  vehicleScheduleData: BookingScheduleInterface[] | undefined,
  setSchedules: (s: BookingScheduleInterface[]) => void
) => {
  const place = placeScheduleWithoutVisitOnly(placeScheduleData);
  const visitOnly = visitOnlyScheduleData ?? [];
  const noPlace = noPlaceZoneBookingScheduleData ?? [];
  const vehicle = vehicleScheduleData ?? [];
  setSchedules([...place, ...visitOnly, ...noPlace, ...vehicle]);
};
